/**
 * Translation queue hook (#2241).
 *
 * Owns the "I pressed Start, the dialog closed, now show the picked
 * language as in-progress until the server reports it finished"
 * flow. Lives one level above `ChangeLanguageModal` so the state
 * survives the modal closing — the AC requires the in-progress row
 * to still be there next time the dialog opens.
 *
 * The backend has no streaming endpoint for job state; the only
 * signal we have is that `useGetSessionDocuments` eventually lists
 * the new document with `finished: true`. We poll by invalidating
 * that query on an interval while the queue is non-empty, with a
 * hard timeout so a wedged job doesn't sit as "in progress" forever
 * (AC: "the failed language does not sit in the list pretending to
 * be in progress").
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useTranslateDocument } from '@utils/api/documents/use-translate-document.tsx';
import { SESSION_DOCUMENTS_QUERY_KEY } from '@utils/api/documents/use-get-session-documents.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';

type PendingEntry = {
  startedAt: number;
};

type PendingLanguages = Record<string, PendingEntry>;

/** How often we recheck for a new finished translation while any job is running. */
export const QUEUE_POLL_INTERVAL_MS = 10_000;
/** Hard cap on how long a single job can stay "in progress". */
export const QUEUE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

interface Args {
  sessionId: string;
  /** Source document id — the translate endpoint is scoped to it. */
  documentId: string;
  /** Filename to carry over to the new translation (optional). */
  filename?: string | null;
  /**
   * Live list of translations the server knows about. When a code we
   * put on the queue shows up here with `finished: true`, we drop it.
   */
  completedLanguages: Array<string | null | undefined>;
  /**
   * Document id the editor header's language picker is reading
   * `useGetDocumentTranslations` with. #2294: when the current pane is
   * a translation, that query is keyed on the translation's own id, not
   * the source id — so the polling tick needs to invalidate both the
   * source and the current-pane queries to refresh the picker.
   */
  currentDocumentId?: string;
}

export interface TranslationQueue {
  /** Upper-case codes that are currently queued. */
  inProgressCodes: string[];
  /** Submit a new translation. Resolves once the mutation returns. */
  start: (code: string) => Promise<void>;
  /** True while any mutation is mid-flight (optimistic UI gating). */
  isStarting: boolean;
}

export function useTranslationQueue({
  sessionId,
  documentId,
  filename,
  completedLanguages,
  currentDocumentId
}: Args): TranslationQueue {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: translate, isLoading: isStarting } =
    useTranslateDocument();

  const [pending, setPending] = useState<PendingLanguages>({});
  // Freshest `pending` snapshot for callbacks that fire outside render.
  const pendingRef = useRef<PendingLanguages>(pending);
  pendingRef.current = pending;

  // Normalised set of completed codes for cheap lookup.
  const completedSet = new Set(
    completedLanguages
      .map(c => c?.toUpperCase())
      .filter((c): c is string => !!c)
  );

  // Drop any pending entry the server now shows as a real finished doc.
  useEffect(() => {
    setPending(prev => {
      let changed = false;
      const next: PendingLanguages = {};
      for (const [code, entry] of Object.entries(prev)) {
        if (completedSet.has(code)) {
          changed = true;
          continue;
        }
        next[code] = entry;
      }
      return changed ? next : prev;
    });
  }, [completedLanguages.join('|')]);

  // While any job is queued, poll for new session documents + timeout
  // sweep. One interval covers both so we don't spawn a timer per
  // code — easier to clean up and matches the backend's model (a
  // single list query, no per-job state).
  useEffect(() => {
    const hasPending = Object.keys(pending).length > 0;
    if (!hasPending || !sessionId) return;

    const tick = () => {
      queryClient.invalidateQueries([SESSION_DOCUMENTS_QUERY_KEY, sessionId]);
      // #2294: refresh `documentTranslations` too — it's the query the
      // editor header's language picker reads, and the picker stayed
      // stale until a full reload because the earlier tick only
      // invalidated `sessionDocuments`.
      queryClient.invalidateQueries(
        QueryKeys.documentTranslations(sessionId, documentId)
      );
      if (currentDocumentId && currentDocumentId !== documentId) {
        queryClient.invalidateQueries(
          QueryKeys.documentTranslations(sessionId, currentDocumentId)
        );
      }

      // Timeout sweep: drop entries that have exceeded the hard cap.
      // Toast once per dropped code so the user knows it didn't land.
      const now = Date.now();
      const dropped: string[] = [];
      setPending(prev => {
        const next: PendingLanguages = {};
        for (const [code, entry] of Object.entries(prev)) {
          if (now - entry.startedAt >= QUEUE_TIMEOUT_MS) {
            dropped.push(code);
            continue;
          }
          next[code] = entry;
        }
        return dropped.length > 0 ? next : prev;
      });
      for (const code of dropped) {
        toast.error(
          t('translation-timed-out', {
            code,
            defaultValue: 'Translation to {{code}} timed out. Please try again.'
          })
        );
      }
    };

    // Fire once immediately so the user doesn't wait a full interval
    // for the first check — the earlier design made the picker lag by
    // up to 10 seconds after the translation actually finished.
    tick();
    const id = window.setInterval(tick, QUEUE_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [pending, sessionId, documentId, currentDocumentId, queryClient, t]);

  const start = useCallback(
    async (code: string) => {
      const normalised = code.toUpperCase();
      // Optimistically add to the queue so the modal (and the dialog
      // the next time it opens) can show the in-progress row even
      // before the mutation returns.
      setPending(prev => {
        if (prev[normalised]) return prev;
        return { ...prev, [normalised]: { startedAt: Date.now() } };
      });

      try {
        await translate({
          sessionId,
          documentId,
          languages: [normalised],
          filename: filename ?? undefined
        });
      } catch {
        // useTranslateDocument toasts the error already. Drop the
        // pretending-in-progress row so the user isn't lied to.
        setPending(prev => {
          if (!prev[normalised]) return prev;
          const next = { ...prev };
          delete next[normalised];
          return next;
        });
        throw new Error('translate-failed');
      }
    },
    [translate, sessionId, documentId, filename]
  );

  return {
    inProgressCodes: Object.keys(pending),
    start,
    isStarting
  };
}
